import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption } from '@/types/filters.types';
import * as adminSvc from '@/services/admin.service';
import type { AdminUser } from '@/services/admin.service';
import { appointmentsService } from '@/services/appointments.service';
import type { AppointmentType as ApptType } from '@/services/appointments.service';
import * as plansSvc from '@/services/plans.service';
import { listTemplates } from '@/services/memberships.service';
import { getFilterOptions } from '@/services/filters.service';
import { buildLeadSourceOptions } from '@/hooks/filters/leadSourceOptions';

export function usePlanFilterOptions(opts?: { enabled?: boolean }): Record<string, FilterOption[]> {
  const enabled = opts?.enabled ?? true;

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: adminSvc.listUsers,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: () => appointmentsService.getAppointmentTypes(),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: planTypes } = useQuery({
    queryKey: ['plan-types'],
    queryFn: adminSvc.listPlanTypes,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: leadSourceCategories } = useQuery({
    queryKey: ['lead-source-categories'],
    queryFn: adminSvc.listLeadSourceCategories,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: membershipTemplates } = useQuery({
    queryKey: ['membership-templates-filter'],
    queryFn: () => listTemplates({ incrementType: 1, limit: 500 }),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: stageStats } = useQuery({
    queryKey: ['stage-stats', {}],
    queryFn: () => plansSvc.getStageStats({}),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: statusOptions } = useQuery({
    queryKey: ['plan-status-options'],
    queryFn: plansSvc.getStatusOptions,
    staleTime: 5 * 60_000,
    enabled,
  });

  // UTM sources come from the shared /filters/options endpoint (distinct DB
  // values) — same source the Leads list uses, so options stay consistent.
  const { data: utmOptions } = useQuery({
    queryKey: ['filter-options', 'utm-source'],
    queryFn: () => getFilterOptions(['utm-source']),
    staleTime: 5 * 60_000,
    enabled,
  });

  return useMemo<Record<string, FilterOption[]>>(() => {
    const doctors = (users ?? [])
      .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('doctor'))
      .map((u: AdminUser) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' }));

    const coordinators = (users ?? [])
      .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('coordinator'))
      .map((u: AdminUser) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' }));

    const apptTypeOpts: FilterOption[] = (() => {
      const seen = new Set<string>();
      return (appointmentTypes ?? [])
        .filter((at: ApptType) => at.appointmentTypeGroupActive !== false)
        .map((at: ApptType) => ({ value: String(at.appointmentTypeId), label: at.appointmentTypeName }))
        .filter((t) => {
          const key = t.label.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    })();

    // Shared builder — the canonical `filtersgroup-<id>` category / `<id>` source
    // shape every lead-source filter uses. This hook previously emitted `g-<id>`
    // for categories, which FilterChecklist does not recognize as a group parent,
    // so the category cascade on the Plans list did nothing.
    const leadSourceOpts = buildLeadSourceOptions(leadSourceCategories);

    return {
      'appt-type': apptTypeOpts,
      'plan-type': (planTypes ?? []).filter((pt) => pt.active).map((pt) => ({ value: String(pt.planTypeId), label: pt.name })),
      'lead-source': leadSourceOpts,
      'membership-type': (membershipTemplates ?? [])
        .filter((t) => !t.title.includes('----'))
        .map((t) => ({ value: String(t.templateId), label: t.title })),
      'plan-stages': (stageStats ?? []).map((s) => ({ value: String(s.stageId), label: s.stageName })),
      'plan-status': (statusOptions ?? []).map((s) => ({ value: String(s.stageSubId), label: s.label })),
      doctor: doctors,
      coordinator: coordinators,
      facility: (facilities ?? []).filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName ?? 'Unknown' })),
      'utm-source': utmOptions?.['utm-source'] ?? [],
    };
  }, [users, facilities, appointmentTypes, planTypes, leadSourceCategories, membershipTemplates, stageStats, statusOptions, utmOptions]);
}
